import { Inject, Injectable } from '@angular/core';

import { Controllers } from '@shared/core/controllers';

import * as Utils from '@shared/core/utils';
import * as Tokens from '@shared/core/tokens';

@Injectable()
export class ConfirmationPageTextFactory {

    constructor(
        @Inject(Tokens.CONFIG_TOKEN) private readonly _config: IConfig,
        private readonly _controllers: Controllers
    ) { }

    public getValueByKey(val: OLO.Types.CONFIRMATION_PAGE_VARIABLE): string {

        switch (val) {
            case 'pickupDate': return this._handlePickupDate();
            case 'orderNumber': return this._handleOrderNumber();
            case 'locationName': return this._handleLocationName();
            case 'orderTypeName': return this._handleOrderTypeName();
            case 'locationAddress': return this._handleLocationAddress();
            case 'orderTypeDetails': return this._handleOrderTypeDetails();
            case 'orderTypeDetailValue': return this._handleOrderTypeDetailValue();
            default:
                return '';
        }

    }

    private _handleOrderNumber(): string {

        const result = this._getOrderId();

        if (result === null || result === undefined || result < 0) {
            this._error('orderNumber');

            return '';
        }

        return `${result}`;
    }

    private _handleLocationName(): string {
        const locationNo = this._getLocationNo();
        let result: string;

        this._controllers.locations.locationFriendlyName$(locationNo).subscribe(
            locationName => result = locationName
        );

        if (!result) {
            this._error('locationName');

            return '';
        }

        return result;
    }

    private _handleLocationAddress(): string {
        const locationNo = this._getLocationNo();
        let result: string;

        this._controllers.locations.locationAddress$(locationNo).subscribe(
            locationAddress => result = locationAddress
        );

        if (!result) {
            this._error('locationAddress');

            return '';
        }

        return result;
    }

    private _handleOrderTypeDetailValue(): string {
        const orderId = this._getOrderId();
        let result: string;

        this._controllers.historyOrders.getHistoryOrderTableNo$(orderId).subscribe(
            tableNo => result = tableNo
        );

        if (!result) {
            this._error('orderTypeDetailValue');

            return '';
        }

        return result;
    }

    private _handlePickupDate(): string {
        const orderId = this._getOrderId();
        const type = 'orderConfirmation';
        const format = 'ddd, Do of MMM';
        let result: string;

        this._controllers.historyOrders.getHistoryOrder$(orderId).subscribe(
            order => result = order?.data?.PickUpDate
        );

        if (!result) {
            this._error('pickupDate');

            return '';
        }

        const converted: Date = Utils.Dates.createDate(result);
        if (converted.toString() === 'Invalid Date') return result;

        return Utils.Pickups.createPickupLabelName(
            this._config.futureOrders,
            type,
            converted,
            Utils.Dates.datesDiffInDays(new Date(), converted) === 0,
            format,
        );
    }

    private _handleOrderTypeName(): string {
        let result: string;

        this._controllers.historyOrders.getOrderTypeDescription$(
            this._getOrderId(),
            this._getLocationNo()
        ).subscribe(
            orderTypeName => result = orderTypeName
        );

        if (!result) {
            this._error('orderTypeName');

            return '';
        }

        return result;
    }

    private _handleOrderTypeDetails(): string {
        let result: string;

        this._controllers.historyOrders.getHistoryOrderDetails$(
            this._getOrderId()
        ).subscribe(
            orderTypeDetils => result = orderTypeDetils
        );

        if (!result) {
            this._error('orderTypeDetails');

            return '';
        }

        return result;
    }

    private _getOrderId(): number {
        let orderId: number;

        this._controllers.routes.extractOrderIdFromQueryParams$().subscribe(
            orderNumber => orderId = orderNumber
        );

        return orderId;
    }

    private _getLocationNo(): number {
        let locationNo: number;

        this._controllers.routes.extractLocationNoFromQueryParams$().subscribe(
            locNo => locationNo = locNo
        );

        return locationNo;
    }

    private _error(variable: OLO.Types.CONFIRMATION_PAGE_VARIABLE): void {
        console.error(`ERROR: ${variable} hasn\`t been assigned any value. No value might be available for this variable in this case`);
    }

}
